import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { sitePopup, files as filesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const KEY_ALWAYS = 'pbb_popup_ignore'; // unused, kept for future
const KEY_SESSION = 'pbb_popup_dismissed_session';
const KEY_USER = 'pbb_popup_dismissed_user';

function shouldShow(cfg, user) {
  const freq = cfg?.frequency || 'session';
  if (freq === 'always') return true;
  if (freq === 'user') {
    if (!user?.id) return true; // guests always see if freq=user
    try {
      const set = JSON.parse(localStorage.getItem(KEY_USER) || '[]');
      return !set.includes(user.id);
    } catch { return true; }
  }
  // session default
  try { return !sessionStorage.getItem(KEY_SESSION); } catch { return true; }
}

function markDismissed(cfg, user) {
  const freq = cfg?.frequency || 'session';
  if (freq === 'user' && user?.id) {
    try {
      const set = JSON.parse(localStorage.getItem(KEY_USER) || '[]');
      if (!set.includes(user.id)) set.push(user.id);
      localStorage.setItem(KEY_USER, JSON.stringify(set));
    } catch {}
  } else if (freq === 'session') {
    try { sessionStorage.setItem(KEY_SESSION, String(Date.now())); } catch {}
  }
}

function resolveImageUrl(u) {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return filesApi.urlFor(u);
}

/** Home-page pop-up. Config now lives in MongoDB and is edited via the
 *  Admin → Pop-up Manager panel — no redeploys required. */
export default function HomePopupBanner() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    sitePopup.public()
      .then(c => {
        if (!c || c.enabled === false) return;
        if (!shouldShow(c, user)) return;
        setCfg(c);
        setTimeout(() => setOpen(true), 300);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]); // eslint-disable-line

  const close = () => {
    markDismissed(cfg, user);
    setOpen(false);
  };

  const handleBtn = (btn) => {
    close();
    const url = (btn.url || '').trim();
    if (!url) return;
    if (btn.target === '_blank' || /^https?:/i.test(url)) {
      window.open(url, btn.target || '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };

  if (!open || !cfg) return null;

  const enabledBtns = (cfg.buttons || []).filter(b => b.enabled && b.label);
  const bg = cfg.bgColor || '#0a140d';
  const fg = cfg.textColor || '#ffffff';
  const img = resolveImageUrl(cfg.imageUrl);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={cfg.headline || 'Welcome'}
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm"
      onClick={close}
      data-testid="home-popup-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: bg, color: fg }}
        data-testid="home-popup-banner"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          data-testid="home-popup-close"
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-white/95 text-neutral-900 shadow-xl border border-neutral-200 hover:bg-white flex items-center justify-center"
        >
          <X size={16} />
        </button>

        {img && (
          <button
            type="button"
            onClick={() => enabledBtns[0] && handleBtn(enabledBtns[0])}
            data-testid="home-popup-image"
            className="block w-full focus:outline-none">
            <img src={img} alt="" className="w-full h-auto block" draggable={false} />
          </button>
        )}

        {(cfg.headline || cfg.body) && (
          <div className="px-5 pt-4 pb-2 text-center">
            {cfg.headline && <h2 className="text-xl md:text-2xl font-black" data-testid="home-popup-headline">{cfg.headline}</h2>}
            {cfg.body && <p className="mt-2 text-sm opacity-90 whitespace-pre-wrap" data-testid="home-popup-body">{cfg.body}</p>}
          </div>
        )}

        {enabledBtns.length > 0 && (
          <div className="p-4 grid gap-2 sm:grid-cols-3" data-testid="home-popup-buttons">
            {enabledBtns.map((b, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleBtn(b)}
                data-testid={`home-popup-btn-${i}`}
                className="w-full px-4 py-2.5 rounded-full bg-green-500 hover:bg-green-400 text-black font-semibold text-sm shadow-lg shadow-green-500/20"
              >
                {b.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
